import React, { useRef, useEffect, useState, useCallback } from "react";
import * as THREE from "three";
import { scrollTo } from "./Navbar";
import HeroBackgroundChart from "./HeroBackgroundChart";

// ————————————————————————————————————————————————
// MBM, Mind Before Markets · Neural Navigation
// A procedural particle brain. Each lobe is a menu region.
// Click a region → the brain rotates to face it and the
// section panel opens. Drag to rotate. Monochrome noir.
// ————————————————————————————————————————————————

const REGIONS = [
  {
    key: "academy",
    anat: "PREFRONTAL CORTEX",
    title: "Academy",
    node: [0.34, 0.5, 0.98],
    blurb:
      "Premium live coaching and mentorship, built around psychology and discipline. Available now.",
    items: ["Live coaching & reviews", "Your AI Coach", "A serious community"],
    cta: "View memberships",
    target: "pricing",
  },
  {
    key: "trainer",
    anat: "PARIETAL LOBE",
    title: "Trading Trainer",
    node: [0.3, 0.86, -0.12],
    blurb:
      "Gamified, step by step learning that takes complete beginners from zero to confident. Coming soon.",
    items: ["Bite sized lessons", "Quizzes & XP", "Learn by doing"],
    cta: "Join the waitlist",
    target: "ecosystem",
  },
  {
    key: "intelligence",
    anat: "TEMPORAL LOBE",
    title: "Market Intelligence",
    node: [0.92, -0.02, 0.28],
    blurb:
      "Daily AI market briefs that explain what is moving, in plain language. Analysis only. Coming soon.",
    items: ["Daily briefs", "Plain language", "Context, not noise"],
    cta: "Join the waitlist",
    target: "ecosystem",
  },
  {
    key: "tools",
    anat: "OCCIPITAL LOBE",
    title: "Indicators & Tools",
    node: [0.3, 0.34, -1.05],
    blurb:
      "A proprietary TradingView suite for analysis and decision support. Never a buy or sell call. Coming soon.",
    items: ["Decision support", "Built on TradingView", "Analysis only"],
    cta: "Join the waitlist",
    target: "ecosystem",
  },
  {
    key: "aipro",
    anat: "CEREBELLUM",
    title: "AI Pro",
    node: [0.34, -0.52, -0.78],
    blurb:
      "A standalone AI trading mentor, trained on the MBM way. Coming soon.",
    items: ["Ask anything, MBM style", "Guided practice", "Always on"],
    cta: "Join the waitlist",
    target: "ecosystem",
  },
];

const sstep = THREE.MathUtils.smoothstep;

// Layered pseudo-noise for cortical folds (gyri / sulci)
function foldNoise(x, y, z) {
  const a = Math.sin(x * 9.2 + z * 4.1) * Math.sin(y * 8.3 + x * 3.7) * Math.sin(z * 7.1 + y * 5.2);
  const b = Math.sin(x * 21.0 + y * 6.0) * Math.sin(z * 19.0 + x * 8.0);
  const c = Math.sin(x * 34.0 + y * 12.0) * Math.sin(z * 30.0 + y * 9.0);
  return a * 0.62 + b * 0.26 + c * 0.12;
}

// Shape a unit-sphere direction into a cerebrum surface point.
// Produces a recognisable brain: elongated bean, two hemispheres split by a
// longitudinal fissure, temporal-lobe bulges, tapered frontal pole, flat base.
function cerebrumPoint(dir) {
  // elongated bean base, long front↔back (z), medium wide (x), short tall (y)
  const p = new THREE.Vector3(dir.x * 0.9, dir.y * 0.8, dir.z * 1.16);

  // taper the frontal pole (front, z>0) so it isn't a fat sphere
  const frontTaper = sstep(p.z, 0.35, 1.16);
  p.x *= 1 - frontTaper * 0.2;
  p.y *= 1 - frontTaper * 0.12;
  // round the occipital pole (back) inward a touch
  const backTaper = sstep(-p.z, 0.55, 1.16);
  p.x *= 1 - backTaper * 0.12;

  // flatten the underside (base of the brain)
  if (p.y < -0.28) p.y = -0.28 + (p.y + 0.28) * 0.4;

  // temporal-lobe bulges: lower sides swell outward
  const temporal = sstep(-p.y, 0.02, 0.4) * sstep(Math.abs(p.x), 0.32, 0.72);
  p.x += Math.sign(p.x || 1) * temporal * 0.07;
  p.y -= temporal * 0.02;

  // longitudinal fissure: carve a deep valley down the top centre and
  // push the two hemispheres apart so left/right lobes read clearly
  const upper = sstep(p.y, -0.08, 0.5);
  const midline = Math.exp(-(p.x * p.x) / 0.008);
  p.y -= midline * upper * 0.3;
  p.x += Math.sign(p.x || 1) * upper * (1 - midline) * 0.05;
  // round the hemispheric crowns
  p.y += (1 - midline) * upper * 0.03;

  // cortical folds along the surface normal
  const n = foldNoise(p.x, p.y, p.z);
  p.addScaledVector(dir, n * 0.055);

  return { p, n };
}

// Cerebellum: tightly-foliated lobe tucked under the back of the brain
function cerebellumPoint(dir) {
  const p = new THREE.Vector3(dir.x * 0.4, dir.y * 0.22, dir.z * 0.34);
  const folia = Math.sin(p.y * 70 + p.x * 6) * 0.7 + foldNoise(p.x * 2.4, p.y * 2.4, p.z * 2.4) * 0.3;
  const n = folia;
  p.addScaledVector(dir, n * 0.022);
  p.x += Math.sign(p.x || 1) * 0.02; // faint vermis split
  p.add(new THREE.Vector3(0, -0.44, -0.74));
  return { p, n: 0.28 + folia * 0.2 };
}

// Brainstem: short tapered stalk descending from the underside
function stemPoint(dir) {
  const a = Math.atan2(dir.z, dir.x);
  const depth = Math.abs(dir.y); // 0..1 down the stalk
  const taper = 1 - depth * 0.45;
  const rr = (0.05 + Math.abs(Math.sin(a)) * 0.015) * taper;
  const p = new THREE.Vector3(
    Math.cos(a) * rr,
    -0.32 - depth * 0.5,
    -0.28 + Math.sin(a) * rr
  );
  return { p, n: 0.22 };
}

export default function BrainMenu() {
  const mountRef = useRef(null);
  const labelRefs = useRef([]);
  const [active, setActive] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [webglFailed, setWebglFailed] = useState(false);
  const stateRef = useRef({ active: null, hovered: null });
  stateRef.current.active = active;
  stateRef.current.hovered = hovered;

  const close = useCallback(() => setActive(null), []);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, mount.clientWidth / mount.clientHeight, 0.1, 50);
    camera.position.set(0, 0.12, 3.4);

    const renderer = (() => {
      try {
        const test = document.createElement("canvas");
        const gl = test.getContext("webgl2") || test.getContext("webgl");
        if (!gl) return null;
        return new THREE.WebGLRenderer({ antialias: true, alpha: true });
      } catch (err) {
        return null;
      }
    })();
    if (!renderer) {
      setWebglFailed(true);
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    scene.add(group);

    const isMobile = mount.clientWidth < 640;
    let baseScale = isMobile ? 0.6 : 0.92;
    let yOffset = isMobile ? 0.62 : 0; // lift the brain up on mobile to fill the empty space
    group.scale.setScalar(baseScale);
    group.position.y = yOffset;

    const COUNT_CEREBRUM = 9800;
    const COUNT_CEREBELLUM = 1700;
    const COUNT_STEM = 500;
    const TOTAL = COUNT_CEREBRUM + COUNT_CEREBELLUM + COUNT_STEM;

    const positions = new Float32Array(TOTAL * 3);
    const shades = new Float32Array(TOTAL);
    const regionIdx = new Float32Array(TOTAL);
    const sizes = new Float32Array(TOTAL);

    const regionVecs = REGIONS.map((r) => new THREE.Vector3(...r.node).normalize());

    let seed = 7;
    const rand = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };

    const dir = new THREE.Vector3();
    for (let i = 0; i < TOTAL; i++) {
      const u = rand() * 2 - 1;
      const t = rand() * Math.PI * 2;
      const s = Math.sqrt(1 - u * u);
      dir.set(s * Math.cos(t), u, s * Math.sin(t));

      const isCerebellum = i >= COUNT_CEREBRUM && i < COUNT_CEREBRUM + COUNT_CEREBELLUM;
      const isStem = i >= COUNT_CEREBRUM + COUNT_CEREBELLUM;
      const { p, n } = isStem
        ? stemPoint(dir)
        : isCerebellum
        ? cerebellumPoint(dir)
        : cerebrumPoint(dir);

      positions[i * 3] = p.x;
      positions[i * 3 + 1] = p.y;
      positions[i * 3 + 2] = p.z;

      shades[i] = THREE.MathUtils.clamp(0.32 + n * 0.42 + rand() * 0.14, 0.08, 0.95);
      sizes[i] = 0.8 + rand() * 1.4;

      if (isCerebellum || isStem) {
        regionIdx[i] = 4;
      } else {
        const pn = p.clone().normalize();
        let best = 0;
        let bd = -2;
        for (let r = 0; r < 4; r++) {
          const d = pn.dot(regionVecs[r]);
          if (d > bd) {
            bd = d;
            best = r;
          }
        }
        regionIdx[i] = best;
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("shade", new THREE.BufferAttribute(shades, 1));
    geo.setAttribute("region", new THREE.BufferAttribute(regionIdx, 1));
    geo.setAttribute("psize", new THREE.BufferAttribute(sizes, 1));

    const uniforms = {
      uGlow: { value: new Float32Array([0, 0, 0, 0, 0]) },
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
      uAccent: { value: new THREE.Color("#6ea8ff") },
    };

    const mat = new THREE.ShaderMaterial({
      uniforms,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: `
        attribute float shade;
        attribute float region;
        attribute float psize;
        uniform float uGlow[5];
        uniform float uPixelRatio;
        varying float vShade;
        varying float vGlow;
        void main() {
          vShade = shade;
          vGlow = uGlow[int(region)];
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = psize * uPixelRatio * (2.1 + vGlow * 1.2) * (140.0 / -mv.z) * 0.02;
        }
      `,
      fragmentShader: `
        uniform vec3 uAccent;
        varying float vShade;
        varying float vGlow;
        void main() {
          vec2 c = gl_PointCoord - 0.5;
          float d = length(c);
          if (d > 0.5) discard;
          float falloff = smoothstep(0.5, 0.05, d);
          float base = vShade * (0.72 + vGlow * 0.9);
          vec3 col = mix(vec3(base), mix(vec3(1.0), uAccent, 0.65), vGlow * 0.7);
          gl_FragColor = vec4(col, falloff * (0.62 + vGlow * 0.5));
        }
      `,
    });

    const points = new THREE.Points(geo, mat);
    group.add(points);

    const nodeMeshes = REGIONS.map((r, i) => {
      const holder = new THREE.Group();
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.014, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0xffffff })
      );
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.035, 0.038, 40),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5, side: THREE.DoubleSide })
      );
      holder.add(dot);
      holder.add(ring);
      const pos = new THREE.Vector3(...r.node).multiplyScalar(1.04);
      holder.position.copy(pos);
      holder.userData.index = i;
      group.add(holder);
      return holder;
    });

    // ————— orbiting market layer: green/red candles + price trace —————
    const mkt = new THREE.Group();
    scene.add(mkt);
    mkt.position.y = yOffset;
    const marketMats = [];
    const candleGroups = [];
    const disposables = [];

    // jagged price-line trace orbiting the brain
    const tracePts = [];
    const segs = 140;
    const tr = 1.98;
    for (let i = 0; i <= segs; i++) {
      const a = (i / segs) * Math.PI * 2;
      const jag = Math.sin(a * 11) * 0.06 + Math.sin(a * 27) * 0.03;
      tracePts.push(new THREE.Vector3(Math.cos(a) * (tr + jag), Math.sin(a * 3) * 0.12, Math.sin(a) * (tr + jag)));
    }
    const traceGeo = new THREE.BufferGeometry().setFromPoints(tracePts);
    const traceMat = new THREE.LineBasicMaterial({ color: 0x8fbfa8, transparent: true, opacity: 0.32, blending: THREE.AdditiveBlending, depthWrite: false });
    traceMat.userData.baseOpacity = 0.32;
    const trace = new THREE.Line(traceGeo, traceMat);
    trace.rotation.x = 0.62;
    mkt.add(trace);
    marketMats.push(traceMat);
    disposables.push(traceGeo, traceMat);

    // ————— firing synapses: pulses travel from core to each lobe —————
    const synCurves = REGIONS.map((r) => {
      const end = new THREE.Vector3(...r.node).multiplyScalar(1.02);
      const mid = end.clone().multiplyScalar(0.5).add(
        new THREE.Vector3((Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3, (Math.random() - 0.5) * 0.3)
      );
      const start = new THREE.Vector3((Math.random() - 0.5) * 0.12, (Math.random() - 0.5) * 0.12, (Math.random() - 0.5) * 0.12);
      return new THREE.CatmullRomCurve3([start, mid, end]);
    });
    const synSeg = [];
    synCurves.forEach((c) => {
      const pts = c.getPoints(18);
      for (let j = 0; j < pts.length - 1; j++) {
        synSeg.push(pts[j].x, pts[j].y, pts[j].z, pts[j + 1].x, pts[j + 1].y, pts[j + 1].z);
      }
    });
    const synGeo = new THREE.BufferGeometry();
    synGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(synSeg), 3));
    const synMat = new THREE.LineBasicMaterial({ color: 0x6ea8ff, transparent: true, opacity: 0.14, blending: THREE.AdditiveBlending, depthWrite: false });
    group.add(new THREE.LineSegments(synGeo, synMat));
    const pulseArr = new Float32Array(REGIONS.length * 3);
    const pulseGeo = new THREE.BufferGeometry();
    pulseGeo.setAttribute("position", new THREE.BufferAttribute(pulseArr, 3));
    // soft round glow sprite for the firing pulses
    const dotCanvas = document.createElement("canvas");
    dotCanvas.width = dotCanvas.height = 64;
    const dctx = dotCanvas.getContext("2d");
    const grd = dctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    grd.addColorStop(0, "rgba(255,255,255,1)");
    grd.addColorStop(0.3, "rgba(159,211,255,0.95)");
    grd.addColorStop(1, "rgba(159,211,255,0)");
    dctx.fillStyle = grd;
    dctx.fillRect(0, 0, 64, 64);
    const dotTex = new THREE.CanvasTexture(dotCanvas);
    const pulseMat = new THREE.PointsMaterial({ color: 0xffffff, map: dotTex, size: 0.13, transparent: true, opacity: 0.95, sizeAttenuation: true, depthWrite: false, blending: THREE.AdditiveBlending });
    group.add(new THREE.Points(pulseGeo, pulseMat));
    disposables.push(synGeo, synMat, pulseGeo, pulseMat, dotTex);
    const pv = new THREE.Vector3();

    let dragging = false;
    let px = 0,
      py = 0;
    let velY = 0;
    let tiltX = 0;
    const onDown = (e) => {
      dragging = true;
      px = e.clientX;
      py = e.clientY;
    };
    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - px;
      const dy = e.clientY - py;
      px = e.clientX;
      py = e.clientY;
      if (stateRef.current.active === null) {
        group.rotation.y += dx * 0.005;
        tiltX = THREE.MathUtils.clamp(tiltX + dy * 0.003, -0.5, 0.5);
        velY = dx * 0.0008;
      }
    };
    const onUp = () => (dragging = false);
    mount.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);

    const front = new THREE.Vector3(0, 0.18, 1).normalize();
    const targetQuat = new THREE.Quaternion();
    let camZTarget = 3.4;
    let nextPop = 1.5, popIdx = -1, popStart = 0;

    const glowCurrent = new Float32Array(5);
    const clock = new THREE.Clock();
    let raf;
    const v = new THREE.Vector3();

    const animate = () => {
      raf = requestAnimationFrame(animate);
      const dt = Math.min(clock.getDelta(), 0.05);
      const t = clock.elapsedTime;
      const st = stateRef.current;

      if (st.active === null) {
        if (!dragging && !reduceMotion) group.rotation.y += (0.12 + velY * 60 * Math.exp(-t)) * dt;
        velY *= 0.95;
        group.rotation.x += (tiltX - group.rotation.x) * 0.08;
        camZTarget = 3.4;
      } else {
        const nodeDir = new THREE.Vector3(...REGIONS[st.active].node).normalize();
        targetQuat.setFromUnitVectors(nodeDir, front);
        group.quaternion.slerp(targetQuat, reduceMotion ? 1 : 0.06);
        camZTarget = 2.7;
      }
      camera.position.z += (camZTarget - camera.position.z) * 0.05;

      for (let i = 0; i < 5; i++) {
        const goal = st.active === i ? 1 : st.hovered === i && st.active === null ? 0.55 : 0;
        glowCurrent[i] += (goal - glowCurrent[i]) * (reduceMotion ? 1 : 0.08);
      }
      uniforms.uGlow.value = glowCurrent;

      nodeMeshes.forEach((m, i) => {
        m.children[1].lookAt(camera.position);
        const pulse = reduceMotion ? 1 : 1 + Math.sin(t * 2 + i) * 0.12;
        const g = 1 + glowCurrent[i] * 0.9;
        m.children[1].scale.setScalar(pulse * g);
        m.children[1].material.opacity = 0.35 + glowCurrent[i] * 0.55;
        m.children[0].scale.setScalar(g);
      });

      const w = mount.clientWidth;
      const h = mount.clientHeight;
      nodeMeshes.forEach((m, i) => {
        const el = labelRefs.current[i];
        if (!el) return;
        v.copy(m.position).applyMatrix4(group.matrixWorld).project(camera);
        const x = (v.x * 0.5 + 0.5) * w;
        const y = (-v.y * 0.5 + 0.5) * h;
        const behind = v.z > 1;
        const worldPos = m.position.clone().applyMatrix4(group.matrixWorld);
        const facing = worldPos.clone().normalize().dot(camera.position.clone().normalize());
        const vis = behind ? 0 : THREE.MathUtils.clamp((facing + 0.15) * 2.2, 0, 1);
        el.style.transform = `translate(${x}px, ${y}px)`;
        el.style.opacity = String(vis * (st.active === null || st.active === i ? 1 : 0.15));
        el.style.pointerEvents = vis > 0.4 ? "auto" : "none";
      });

      // orbiting market layer
      if (!reduceMotion) {
        mkt.rotation.y += dt * 0.12;
        mkt.rotation.z = Math.sin(t * 0.1) * 0.05;
      }
      const dimT = st.active === null ? 1 : 0.18;
      marketMats.forEach((m) => {
        m.opacity += (m.userData.baseOpacity * dimT - m.opacity) * 0.08;
      });

      // candle "heartbeat", one candle pops & flashes softly
      if (!reduceMotion && st.active === null && candleGroups.length) {
        if (t > nextPop) {
          if (popIdx >= 0) candleGroups[popIdx].g.scale.setScalar(1);
          popIdx = Math.floor(Math.random() * candleGroups.length);
          popStart = t;
          nextPop = t + 2.2 + Math.random() * 1.8;
        }
        if (popIdx >= 0) {
          const pp = (t - popStart) / 0.7;
          const cg = candleGroups[popIdx];
          if (pp < 1) {
            const e = Math.sin(pp * Math.PI);
            cg.g.scale.set(1 + e * 0.5, 1 + e * 0.9, 1 + e * 0.5);
            cg.mats.forEach((m) => (m.opacity = Math.min(1, m.opacity + e * 0.6)));
          } else {
            cg.g.scale.setScalar(1);
          }
        }
      } else if (popIdx >= 0) {
        candleGroups[popIdx].g.scale.setScalar(1);
      }

      // firing synapses (core -> lobes)
      for (let i = 0; i < synCurves.length; i++) {
        const speed = st.active === i || st.hovered === i ? 0.7 : 0.24;
        const tt = (t * speed + i * 0.37) % 1;
        synCurves[i].getPointAt(tt, pv);
        pulseArr[i * 3] = pv.x;
        pulseArr[i * 3 + 1] = pv.y;
        pulseArr[i * 3 + 2] = pv.z;
      }
      pulseGeo.attributes.position.needsUpdate = true;

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      baseScale = w < 640 ? 0.6 : 0.92;
      yOffset = w < 640 ? 0.62 : 0;
      group.scale.setScalar(baseScale);
      group.position.y = yOffset;
      mkt.position.y = yOffset;
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      mount.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      geo.dispose();
      mat.dispose();
      disposables.forEach((d) => d.dispose && d.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
  }, []);

  const region = active !== null ? REGIONS[active] : null;

  const handleCta = () => {
    if (!region) return;
    const target = region.target;
    setActive(null);
    setTimeout(() => scrollTo(target), 120);
  };

  return (
    <div id="hero" style={styles.root} data-testid="brain-menu">
      <style>{css}</style>

      {/* moving chart underlay */}
      <div style={styles.bgChart}>
        <HeroBackgroundChart />
      </div>

      {webglFailed ? (
        <div className="mbm-fallback" data-testid="brain-fallback">
          <div className="mbm-fallback-sub">
            Master the trader within. <em>The markets will follow.</em>
          </div>
          <div className="mbm-fallback-grid">
            {REGIONS.map((r, i) => (
              <button
                key={r.key}
                className="mbm-fallback-item"
                onClick={() => setActive(i)}
                data-testid={`brain-region-${r.key}`}
              >
                <span className="mbm-label-anat">{r.anat}</span>
                <span className="mbm-fallback-item-title">{r.title}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* 3D stage */}
          <div ref={mountRef} style={styles.stage} data-testid="brain-stage" />

          {/* hero content: headline + subheading + CTAs */}
          <div className="mbm-hero-content" style={styles.heroContent}>
            <div className="mbm-hero-eyebrow section-label" data-testid="hero-eyebrow">
              MBM · Mind Before Markets
            </div>
            <h1 className="mbm-hero-h1">
              Master Your Mind.
              <br />
              Master The <span className="blue-text">Markets.</span>
            </h1>
            <p className="mbm-hero-sub">
              We build disciplined traders through education, structure and accountability.
            </p>
            <div className="mbm-hero-cta">
              <button
                className="btn-solid px-8 py-3.5 text-sm font-medium tracking-wide"
                onClick={() => scrollTo("apply")}
                data-testid="hero-apply-btn"
              >
                Apply Now
              </button>
              <button
                className="btn-glass text-white px-8 py-3.5 text-sm font-medium tracking-wide"
                onClick={() => scrollTo("academy")}
                data-testid="hero-academy-btn"
              >
                Explore The Academy
              </button>
            </div>
            <div className="mbm-hero-hint">
              {active === null ? "Drag to rotate · click a region" : "\u00A0"}
            </div>
          </div>

          {/* region labels */}
          {REGIONS.map((r, i) => (
            <button
              key={r.key}
              ref={(el) => (labelRefs.current[i] = el)}
              className="mbm-label"
              style={{ opacity: 0 }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => setActive(i)}
              data-testid={`brain-region-${r.key}`}
            >
              <span className="mbm-label-dot" />
              <span className="mbm-label-text">
                <span className="mbm-label-anat">{r.anat}</span>
                {r.title}
              </span>
            </button>
          ))}
        </>
      )}

      {/* panel */}
      <aside className={`mbm-panel ${region ? "open" : ""}`} data-testid="brain-panel">
        {region && (
          <div className="mbm-panel-inner">
            <button className="mbm-close" onClick={close} aria-label="Close panel" data-testid="brain-panel-close">
              ×
            </button>
            <div className="mbm-panel-anat">{region.anat}</div>
            <h2 className="mbm-panel-title">{region.title}</h2>
            <p className="mbm-panel-blurb">{region.blurb}</p>
            <ul className="mbm-panel-list">
              {region.items.map((it) => (
                <li key={it}>{it}</li>
              ))}
            </ul>
            <button className="mbm-cta" onClick={handleCta} data-testid="brain-panel-cta">
              {region.cta} →
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

const styles = {
  root: {
    position: "relative",
    width: "100%",
    height: "100vh",
    background: "radial-gradient(ellipse 80% 60% at 50% 42%, #131313 0%, #0A0A0A 65%)",
    overflow: "hidden",
    color: "#EDEDED",
  },
  stage: { position: "absolute", inset: 0, zIndex: 1, cursor: "grab", touchAction: "none" },
  bgChart: { position: "absolute", inset: 0, zIndex: 0, opacity: 0.6, pointerEvents: "none" },
  heroContent: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: "9%",
    zIndex: 5,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "0 24px",
    pointerEvents: "none",
  },
  tagline: {
    position: "absolute",
    left: "clamp(20px, 4vw, 44px)",
    bottom: "clamp(52px, 8vw, 76px)",
    fontFamily: "'Instrument Serif', serif",
    fontSize: "clamp(22px, 3.4vw, 34px)",
    maxWidth: 380,
    lineHeight: 1.15,
    opacity: 0.92,
    zIndex: 5,
    pointerEvents: "none",
  },
  hint: {
    position: "absolute",
    bottom: "clamp(16px, 3vw, 28px)",
    left: 0,
    right: 0,
    textAlign: "center",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 10,
    letterSpacing: "0.3em",
    opacity: 0.35,
    zIndex: 5,
    pointerEvents: "none",
  },
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Archivo:wght@400;500&family=JetBrains+Mono:wght@400;700&display=swap');

.mbm-label {
  position: absolute; top: 0; left: 0; z-index: 6;
  display: flex; align-items: center; gap: 10px;
  background: none; border: none; padding: 6px; margin: 0;
  color: #EDEDED; cursor: pointer;
  transition: opacity .25s ease;
  transform: translate(-9999px, -9999px);
}
.mbm-label:focus-visible { outline: 1px solid rgba(255,255,255,.6); outline-offset: 2px; }
.mbm-label-dot { width: 5px; height: 5px; border-radius: 50%; background: #fff; flex: none; box-shadow: 0 0 8px rgba(255,255,255,.6); }
.mbm-label-text { display: flex; flex-direction: column; align-items: flex-start; text-align: left; font-family: 'JetBrains Mono', monospace; font-size: 12px; letter-spacing: .14em; text-transform: uppercase; }
.mbm-label-anat { font-family: 'JetBrains Mono', monospace; font-size: 8px; letter-spacing: .25em; opacity: .45; }
.mbm-label:hover .mbm-label-anat { opacity: .8; }

.mbm-hero-eyebrow {
  margin-bottom: 18px; color: #9fd3ff; font-size: 11px; letter-spacing: 0.32em;
  text-shadow: 0 2px 24px rgba(8,8,9,0.9);
}
.mbm-hero-h1 {
  font-family: 'Cabinet Grotesk', 'Helvetica Neue', sans-serif;
  font-weight: 700; letter-spacing: -0.02em; line-height: 0.95;
  font-size: clamp(32px, 6vw, 76px); color: #FAFAFA;
  text-shadow: 0 6px 60px rgba(8,8,9,0.95);
}
.mbm-hero-sub {
  margin-top: 18px; font-family: 'Satoshi','Inter',sans-serif; font-weight: 300;
  font-size: clamp(14px, 1.6vw, 18px); color: #a1a1aa; max-width: 540px;
  text-shadow: 0 2px 30px rgba(8,8,9,0.9);
}
.mbm-hero-cta {
  margin-top: 30px; display: flex; gap: 14px; flex-wrap: wrap; justify-content: center;
  pointer-events: auto;
}
.mbm-hero-hint {
  margin-top: 22px; font-family: 'JetBrains Mono', monospace; font-size: 10px;
  letter-spacing: .28em; text-transform: uppercase; color: #52525b;
}

.mbm-fallback {
  position: absolute; inset: 0; z-index: 6;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 96px 24px 48px;
}
.mbm-fallback-sub {
  font-family: 'Instrument Serif', serif;
  font-size: clamp(24px, 4vw, 40px); line-height: 1.15; opacity: .92; max-width: 620px;
}
.mbm-fallback-grid {
  margin-top: 40px; width: 100%; max-width: 720px;
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px;
}
.mbm-fallback-item {
  display: flex; flex-direction: column; align-items: flex-start; gap: 6px;
  background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.1);
  color: #EDEDED; padding: 18px 20px; cursor: pointer; text-align: left;
  transition: border-color .25s, background .25s;
}
.mbm-fallback-item:hover { border-color: rgba(110,168,255,.5); background: rgba(255,255,255,.05); }
.mbm-fallback-item-title {
  font-family: 'JetBrains Mono', monospace; font-size: 13px; letter-spacing: .12em; text-transform: uppercase;
}

.mbm-panel {
  position: absolute; top: 0; right: 0; bottom: 0; z-index: 10;
  width: min(420px, 92vw);
  background: rgba(10,10,10,.82);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-left: 1px solid rgba(255,255,255,.08);
  transform: translateX(105%);
  transition: transform .55s cubic-bezier(.22,.9,.24,1);
  display: flex; align-items: center;
}
.mbm-panel.open { transform: translateX(0); }
.mbm-panel-inner { padding: clamp(28px, 5vw, 52px); width: 100%; position: relative; }
.mbm-close {
  position: absolute; top: -8px; right: 8px;
  background: none; border: 1px solid rgba(255,255,255,.18); border-radius: 50%;
  width: 36px; height: 36px; color: #EDEDED; font-size: 18px; cursor: pointer;
  transition: border-color .2s;
}
.mbm-close:hover { border-color: rgba(255,255,255,.6); }
.mbm-panel-anat { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: .3em; opacity: .45; margin-bottom: 14px; }
.mbm-panel-title { font-family: 'Instrument Serif', serif; font-size: clamp(38px, 6vw, 52px); font-weight: 400; margin: 0 0 18px; line-height: 1; }
.mbm-panel-blurb { font-size: 15px; line-height: 1.65; opacity: .8; margin: 0 0 26px; }
.mbm-panel-list { list-style: none; padding: 0; margin: 0 0 34px; }
.mbm-panel-list li {
  font-family: 'JetBrains Mono', monospace; font-size: 12px; letter-spacing: .06em;
  padding: 12px 0; border-top: 1px solid rgba(255,255,255,.1); opacity: .85;
}
.mbm-panel-list li:last-child { border-bottom: 1px solid rgba(255,255,255,.1); }
.mbm-cta {
  font-family: 'JetBrains Mono', monospace; font-size: 12px; letter-spacing: .18em; text-transform: uppercase;
  background: #EDEDED; color: #0A0A0A; border: none; padding: 16px 26px; cursor: pointer;
  transition: background .2s;
}
.mbm-cta:hover { background: #fff; }

@media (max-width: 640px) {
  .mbm-hero-content { bottom: 4% !important; }
  .mbm-panel {
    top: auto; left: 0; right: 0; width: 100%; max-height: 72vh;
    border-left: none; border-top: 1px solid rgba(255,255,255,.1);
    transform: translateY(105%);
    align-items: flex-start; overflow-y: auto;
  }
  .mbm-panel.open { transform: translateY(0); }
  .mbm-tagline { font-size: 20px !important; max-width: 260px !important; }}

@media (prefers-reduced-motion: reduce) {
  .mbm-panel { transition: none; }
}
`;
